<!-- Orbi conversation thread (PCSA-13160): scrolling feed + input. -->
<div class="orbi-thread">
  <div #feed class="orbi-thread__feed" (scroll)="onFeedScroll()">
    <!-- Welcome section (PCSA-13159 / PCSA-13401, Figma "AI Agent Canvas"). The
         greeting is a PERSISTENT header at the top of the conversation area — it
         is NOT an empty state: the Figma mock shows it above an active thread,
         and mobile keeps its `showInitialOptions` header for the whole session
         (it is never cleared by sending). It is hidden only while a guardrail
         locks the chat, where "How can I help you?" would contradict the
         read-only notice.
         The helper line under the subtitle keeps its own, much narrower rule
         (PCSA-13445, `newConversation$`): it is an entry ramp for a patient who
         has not asked anything yet, so it goes for good once a turn exists, and
         while the user is typing or a send is in flight. The suggested PROMPTS
         are no longer part of this block — they follow the conversation instead
         (see below the feed's turns). -->
    <div *ngIf="(chatLocked$ | async) === false" class="orbi-thread__welcome">
      <div class="orbi-thread__greeting">
        <h2 class="orbi-thread__greeting-title">{{ greetingTitle$ | async }}</h2>
        <p class="orbi-thread__greeting-subtitle">How can I help you?</p>
        <p *ngIf="newConversation$ | async" class="orbi-thread__greeting-help">
          Ask about your test results, health, bills, or appointments.
        </p>
      </div>
    </div>

    <!-- Conversation date (design review 08/20: "add conversation date to chat").
         Sits directly above the first turn, the way a date divider does in every
         chat client — not in the panel header, where it would date whatever is on
         screen rather than the conversation.

         `as` on the async pipe is the whole visibility rule: `conversationDate$`
         resolves to '' for a conversation with no turns yet (nothing to date, and
         the greeting stands alone in the initial-state frame), and '' is falsy, so
         the divider appears with the first message and never labels an empty state.

         A resumed conversation shows ITS date, a new one shows "Today" — see
         `conversationDate$`. The hidden prefix gives a screen reader the context
         the layout gives a sighted user; without it the row is announced as a bare
         "Friday, Aug 14, 2026" between two messages. -->
    <p *ngIf="conversationDate$ | async as conversationDate" class="orbi-thread__date">
      <span class="orbi-thread__date-sr">Conversation date </span>{{ conversationDate }}
    </p>

    <patient-ui-orbi-message-item
      *ngFor="let message of messages$ | async; trackBy: trackByIndex"
      [message]="message"
      [insuranceEligibility]="insuranceEligibility"
      [checkedInConfirmationNumbers]="checkedInConfirmationNumbers"
      class="orbi-thread__item"
      (retry)="onRetry()"
      (navigate)="onCardNavigate($event)"
      (appointmentNavigate)="onAppointmentNavigate($event)"
      (appointmentsSeeAll)="onAppointmentsSeeAll()"
      (findLabSearch)="onFindLabSearch($event)"
      (visitHistorySeeAll)="onVisitHistorySeeAll()"
      (rateVisit)="onRateVisit()"
      (findABillFound)="onFindABillFound($event)"
      (addToWallet)="onAddToWallet($event)"
    ></patient-ui-orbi-message-item>

    <!-- Suggested prompts (PCSA-13159), LAST in the feed. The Figma canvas draws
         them under the newest response and above the disclaimer, and they are not
         the empty state of a new conversation: they come back after EVERY answer
         (AC 1.4, restated by the 08/20 design review — "prompts rendering above the
         input field after each response, except guardrail") and they go the moment
         the user types (AC 1.1), returning when the field is cleared again (AC 1.3).
         See `showPrompts$` for the full rule. On an empty conversation this is still
         the element directly under the greeting, which is the initial-state frame,
         and there they show unconditionally.

         `hidden`, not `*ngIf`: the chip set is a random few of thirteen, so
         destroying the component on each keystroke re-rolled the list under the
         user (and rebuilt seven ui-buttons per character). `hidden` also keeps them
         out of the a11y tree while hidden, which `[class]`-based hiding would not.
         The set is re-picked on `promptsKey$` instead — the conversation plus its
         answer count, so a landed response rotates the chips and a keystroke still
         does not (design review 08/28; see `promptsKey$` for why the answer count
         and not the turn count).

         The "How does this work?" chip is the ONE exception to "the prompts follow
         the conversation" (design review 08/19): it is an explainer for a patient
         who has not used the agent yet, so it is offered on a new conversation only
         and retires as soon as a turn exists — including the explainer's own turn.
         The real prompts are unaffected.

         `mousedown` + preventDefault keeps the caret in the input when a chip is
         pressed: picking a prompt fills the field, and losing focus to the button
         first would move the user out of the very field the chip just populated
         (Safari does not focus buttons on click at all, so the two browsers would
         also disagree).

         `#prompts` is read back by the container: the row is the LAST thing in the
         feed, so a row that appears without any TURN changing lands below the fold
         and is scrolled into view by hand (`revealPrompts`). -->

    <patient-ui-orbi-suggested-prompts
      #prompts
      class="orbi-thread__prompts"
      [hidden]="(showPrompts$ | async) === false"
      [align]="(threadEmpty$ | async) ? 'center' : 'start'"
      [showHowItWorks]="(threadEmpty$ | async) === true"
      [conversationKey]="promptsKey$ | async"
      (promptSelected)="onPromptSelected($event)"
      (howItWorks)="onHowItWorks()"
      (mousedown)="$event.preventDefault()"
    ></patient-ui-orbi-suggested-prompts>
  </div>

  <!-- Floating scroll-to-latest (AC 11): shown only when scrolled away.

       The zero-height anchor is what positions it. The button used to be absolute
       against the whole thread at a fixed `bottom`, a distance measured off the
       single-line composer — so once the field grew (PCSA-13548 lets it reach
       ~5 lines) the button stayed put while the input grew up past it, and it
       rendered ON TOP of the text being typed. The anchor is an empty flex item
       sitting exactly on the feed/composer seam, so "just above the input" is
       expressed as a position rather than as a number that has to be kept in step
       with the composer's height. -->
  <div class="orbi-thread__scroll-anchor">
    <button
      *ngIf="showScrollToBottom"
      type="button"
      class="orbi-thread__scroll-bottom material-icons-round"
      aria-label="Scroll to latest message"
      (click)="scrollToBottom()"
    >
      arrow_downward
    </button>
  </div>

  <!-- The "How does this work?" explainer (PCSA-13159 AC 6) renders in the FEED
       above, as an ordinary turn pair: the chip's label as the user's bubble, the
       explainer as an agent response with the copy/thumbs row (design review
       08/11 — the Figma "About Ask AI" frame). It was previously an ephemeral
       note pinned here above the input, which read as a system aside rather than
       an answer to the question the chip asks, and carried no feedback actions.
       The turns come from the store (`explainHowItWorks`). -->

  <!-- Disclaimer sits directly above the input (Figma: feed -> disclaimer ->
       input, all pinned to the bottom of the panel). -->
  <p class="orbi-thread__disclaimer">
    <span class="orbi-thread__disclaimer-icon material-icons-round" aria-hidden="true">auto_awesome</span>
    <span>Check information. AI can make mistakes.</span>
  </p>

  <p *ngIf="chatLocked$ | async" class="orbi-thread__locked-notice" role="status" aria-live="polite">
    This conversation is now read-only. To start a new conversation,
    <button type="button" class="orbi-thread__locked-sign-out" (click)="onSignOut()">sign out</button>
    and back in.
  </p>

  <div class="orbi-thread__input">
    <!-- The in-panel input carries the SAME leading Orbi AI mark as the on-page
         entry bar (PCSA-13406): Figma's input node shows the swirl at the start
         of the field, before the placeholder. It was previously passed only by
         the entry point, so the panel's field rendered without it.

         `disabled` is the LOCKED case ONLY — it no longer includes "a send is in
         flight" (PCSA-13312, 08/14 Vibe). That one binding was the whole of three
         reported issues, because the input derives its blocked cursor, its mic's
         disabled state and its send gate from this single input:
           - "while the agent is thinking, the cursor changes to a stop/blocked
              symbol. It shouldn't — the user can still do other things";
           - "the mic is disabled while the agent is thinking; it should remain
              accessible during loading";
           - "ensure the user can type and send a second message while the agent
              is still generating the first".
         The queue behind it is the effect's `concatMap` plus the answer SLOT the
         reducer reserves per outstanding send (`selectOrbiSendPending` counts
         them); a guardrail lock still disables everything, which is PCSA-13170's
         requirement and unaffected. -->
    <patient-ui-orbi-message-input
      leadingIconSrc="assets/images/orbi-ai-icon.svg"
      [disabled]="(chatLocked$ | async) === true"
      [locked]="(chatLocked$ | async) === true"
      [placeholder]="(chatLocked$ | async) ? 'Feature Unavailable' : 'Ask a Question'"
      (send)="onSend($event)"
      (emptyChange)="onInputEmptyChange($event)"
      (signOut)="onSignOut()"
    ></patient-ui-orbi-message-input>
  </div>
</div>
